import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, DatePicker, InputNumber, Spin, Tooltip } from 'antd';
import {
  buildAvailabilityContext,
  buildBuyerRequirementContext,
  computeInclusiveEndFromStartAndUnits,
  durationTypeLabel,
  inferLeaseUnitsFromRange,
  isDateUnavailableForSeller,
} from '../../utils/infrastructureListingAvailability';
import '../../assets/styles/infrastructure_leasing_availability_calendar.scss';

const InlineRangePanel = DatePicker._InternalRangePanelDoNotUseOrYouWillBeFired;

function LegendItem({ color, label }) {
  return (
    <span className="ile-avail-legend-item d-inline-flex align-items-center gap-2 me-3 mb-1">
      <span className="ile-avail-legend-swatch" style={{ background: color }} aria-hidden />
      <span className="small">{label}</span>
    </span>
  );
}

function clampEndToSellerWindow(end, sellerCtx) {
  if (!end?.isValid?.()) return end;
  if (end.isAfter(sellerCtx.windowEndInclusive.startOf('day'))) {
    return sellerCtx.windowEndInclusive.startOf('day');
  }
  return end.startOf('day');
}

/**
 * Calendar + seller/buyer availability summaries + lease duration control for requirement quotations.
 */
export default function InfrastructureLeasingListingAvailabilityStep({
  listingForm,
  listingInventoryAvailability,
  listingLoading,
  listingError,
  buyerForm,
  buyerLoading,
  buyerError,
  rangeValue,
  onRangeChange,
}) {
  const sellerCtx = useMemo(
    () =>
      buildAvailabilityContext(listingForm || {}, {
        inventoryAvailability: listingInventoryAvailability || undefined,
      }),
    [listingForm, listingInventoryAvailability]
  );
  const buyerCtx = useMemo(() => buildBuyerRequirementContext(buyerForm || {}), [buyerForm]);

  const [durationUnits, setDurationUnits] = useState(1);

  const disabledDate = useCallback((current) => isDateUnavailableForSeller(current, sellerCtx), [sellerCtx]);

  useEffect(() => {
    const u = inferLeaseUnitsFromRange(rangeValue?.[0], rangeValue?.[1], sellerCtx.durationType);
    if (u != null && Number.isFinite(u)) {
      setDurationUnits(u);
    }
  }, [rangeValue, sellerCtx.durationType]);

  const applyDurationFromInput = useCallback(
    (units) => {
      const u = Number(units);
      setDurationUnits(u);
      const start = rangeValue?.[0];
      if (!start?.isValid?.() || !Number.isFinite(u) || u <= 0) return;
      let end = computeInclusiveEndFromStartAndUnits(start, u, sellerCtx.durationType);
      if (!end?.isValid?.()) return;
      end = clampEndToSellerWindow(end, sellerCtx);
      onRangeChange?.([start.startOf('day'), end]);
    },
    [rangeValue, sellerCtx, onRangeChange]
  );

  const onPanelChange = useCallback(
    (dates) => {
      onRangeChange?.(dates);
    },
    [onRangeChange]
  );

  const cellRender = useCallback(
    (current, info) => {
      if (info.type !== 'date') return info.originNode;
      const unavailable = disabledDate(current);
      const afterBuyerDeadline =
        !unavailable &&
        buyerCtx.requiredBy &&
        current.startOf('day').isAfter(buyerCtx.requiredBy.startOf('day'));
      let title = 'Available — within your listing window';
      if (unavailable) {
        title = 'Not available — outside listing window, outside a slot, or booked';
      } else if (afterBuyerDeadline) {
        title = 'Partial match — date is after the buyer required-by date for lease start';
      }
      const wrapClass = unavailable ? '' : afterBuyerDeadline ? 'ile-avail-cell--warn' : 'ile-avail-cell--ok';
      return (
        <Tooltip title={title}>
          <span className={`ile-avail-cell-wrap ${wrapClass}`}>{info.originNode}</span>
        </Tooltip>
      );
    },
    [disabledDate, buyerCtx.requiredBy]
  );

  if (listingLoading || buyerLoading) {
    return (
      <div className="text-center py-5">
        <Spin tip={listingLoading ? 'Loading listing availability…' : 'Loading buyer requirement…'} />
      </div>
    );
  }

  if (listingError) {
    return (
      <Alert
        type="warning"
        showIcon
        className="mb-0"
        message="Could not load listing details"
        description={<p className="mb-0 small">{listingError}</p>}
      />
    );
  }

  const unitLabel = durationTypeLabel(sellerCtx.durationType);
  const pair = Array.isArray(rangeValue) ? rangeValue : null;
  const startDate = pair?.[0];
  const computedEnd =
    startDate?.isValid?.() && Number.isFinite(durationUnits) && durationUnits > 0
      ? clampEndToSellerWindow(
          computeInclusiveEndFromStartAndUnits(startDate, durationUnits, sellerCtx.durationType),
          sellerCtx
        )
      : null;

  return (
    <div className="ile-avail-step">
      <div className="ile-avail-calendar-panel border rounded overflow-hidden mb-3 bg-white">
        <div className="ile-avail-calendar-panel__inner">
          <InlineRangePanel
            value={rangeValue}
            onChange={onPanelChange}
            disabledDate={disabledDate}
            cellRender={cellRender}
            format="YYYY-MM-DD"
            allowEmpty={[false, false]}
            className="ile-avail-inline-range w-100"
            placement="bottomLeft"
            getPopupContainer={() => document.body}
            popupStyle={{ zIndex: 10050 }}
          />
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <span className="form-label small fw-semibold d-block mb-1">Lease length ({unitLabel.toLowerCase()})</span>
          <InputNumber
            className="w-100"
            min={0.01}
            step={sellerCtx.durationType === 'days' ? 1 : 0.25}
            value={durationUnits}
            onChange={(v) => {
              if (v == null || v === '') return;
              applyDurationFromInput(v);
            }}
          />
          <p className="small text-muted mb-0 mt-1">
            Pick a start date in the calendar, then adjust length — end date updates automatically (clamped to your listing
            window).
          </p>
        </div>
        <div className="col-md-6">
          <span className="form-label small fw-semibold d-block mb-1">Computed end date</span>
          <div className="form-control bg-light small py-2 mb-0">
            {computedEnd?.isValid?.() ? computedEnd.format('YYYY-MM-DD') : '—'}
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-lg-6">
          <div className="border rounded p-3 h-100 bg-light bg-opacity-50">
            <h3 className="h6 mb-2">Your listing (seller)</h3>
            <dl className="row small mb-0">
              <dt className="col-sm-5 text-muted">Duration type</dt>
              <dd className="col-sm-7">{unitLabel}</dd>
              <dt className="col-sm-5 text-muted">Available for (total)</dt>
              <dd className="col-sm-7">
                {sellerCtx.totalUnits} {unitLabel.toLowerCase()}
              </dd>
              <dt className="col-sm-5 text-muted">Available from</dt>
              <dd className="col-sm-7">{sellerCtx.availableFrom.format('YYYY-MM-DD')}</dd>
              <dt className="col-sm-5 text-muted">Window ends</dt>
              <dd className="col-sm-7">{sellerCtx.windowEndInclusive.format('YYYY-MM-DD')}</dd>
              {sellerCtx.slotRanges?.length ? (
                <>
                  <dt className="col-sm-5 text-muted">Slots</dt>
                  <dd className="col-sm-7">{sellerCtx.slotRanges.length} published segment(s)</dd>
                </>
              ) : null}
              {sellerCtx.minTenure != null ? (
                <>
                  <dt className="col-sm-5 text-muted">Min tenure</dt>
                  <dd className="col-sm-7">
                    {sellerCtx.minTenure} {unitLabel.toLowerCase()}
                  </dd>
                </>
              ) : null}
              {sellerCtx.maxTenure != null ? (
                <>
                  <dt className="col-sm-5 text-muted">Max tenure</dt>
                  <dd className="col-sm-7">
                    {sellerCtx.maxTenure} {unitLabel.toLowerCase()}
                    {sellerCtx.flexibleTenure ? ' (flexible)' : ''}
                  </dd>
                </>
              ) : null}
            </dl>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="border rounded p-3 h-100 bg-light bg-opacity-50">
            <h3 className="h6 mb-2">Buyer requirement</h3>
            {buyerError ? (
              <p className="small text-warning mb-0">{buyerError}</p>
            ) : (
              <dl className="row small mb-0">
                <dt className="col-sm-5 text-muted">Duration type</dt>
                <dd className="col-sm-7">{durationTypeLabel(buyerCtx.durationType)}</dd>
                {buyerCtx.requiredBy ? (
                  <>
                    <dt className="col-sm-5 text-muted">Required by</dt>
                    <dd className="col-sm-7">{buyerCtx.requiredBy.format('YYYY-MM-DD')} (lease start)</dd>
                  </>
                ) : (
                  <>
                    <dt className="col-sm-5 text-muted">Required by</dt>
                    <dd className="col-sm-7 text-muted">—</dd>
                  </>
                )}
                <dt className="col-sm-5 text-muted">Duration range</dt>
                <dd className="col-sm-7">
                  {buyerCtx.minDuration != null || buyerCtx.maxDuration != null
                    ? `${buyerCtx.minDuration ?? '—'} – ${buyerCtx.maxDuration ?? '—'} ${durationTypeLabel(
                        buyerCtx.durationType
                      ).toLowerCase()}`
                    : '—'}
                </dd>
              </dl>
            )}
          </div>
        </div>
      </div>

      <div className="ile-avail-legend mb-0 py-2 px-2 border rounded bg-white">
        <span className="small fw-semibold d-block mb-1">Legend</span>
        <div className="d-flex flex-wrap">
          <LegendItem color="#d1e7dd" label="Available (listing)" />
          <LegendItem color="#fff3cd" label="After buyer required-by start" />
          <LegendItem color="#e2e3e5" label="Not available" />
          <LegendItem color="#cfe2ff" label="Selected range" />
        </div>
      </div>
    </div>
  );
}
